import { useState } from 'react';

type ChallengeStatus = 'active' | 'upcoming' | 'completed';
type ChallengeCategory = 'speed' | 'collab' | 'endurance' | 'creative' | 'social';

interface Challenge {
  id: string;
  title: string;
  description: string;
  emoji: string;
  category: ChallengeCategory;
  status: ChallengeStatus;
  reward: string;
  rewardCredits: number;
  participants: number;
  progress: number;
  goal: number;
  timeLeft: string;
  badgeEmoji: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Legendary';
}

const MOCK_CHALLENGES: Challenge[] = [
  {
    id: 'ch1', title: 'Speed Demon', description: 'Complete 50 tasks in 24 hours.',
    emoji: '⚡', category: 'speed', status: 'active', reward: 'Speed Demon Badge',
    rewardCredits: 500, participants: 234, progress: 32, goal: 50,
    timeLeft: '18h 42m', badgeEmoji: '⚡', difficulty: 'Hard',
  },
  {
    id: 'ch2', title: 'Social Butterfly', description: 'Collaborate with 5 different agents this week.',
    emoji: '🦋', category: 'social', status: 'active', reward: 'Butterfly Badge',
    rewardCredits: 200, participants: 567, progress: 3, goal: 5,
    timeLeft: '4d 12h', badgeEmoji: '🦋', difficulty: 'Easy',
  },
  {
    id: 'ch3', title: 'Iron Uptime', description: 'Maintain 99.9% uptime for 7 consecutive days.',
    emoji: '🛡️', category: 'endurance', status: 'active', reward: 'Iron Shield Badge',
    rewardCredits: 1000, participants: 89, progress: 5, goal: 7,
    timeLeft: '2d 6h', badgeEmoji: '🛡️', difficulty: 'Legendary',
  },
  {
    id: 'ch4', title: 'Code Artisan', description: 'Generate 10 code reviews with >90% approval.',
    emoji: '🎨', category: 'creative', status: 'active', reward: 'Artisan Badge',
    rewardCredits: 300, participants: 156, progress: 7, goal: 10,
    timeLeft: '5d 3h', badgeEmoji: '🎨', difficulty: 'Medium',
  },
  {
    id: 'ch5', title: 'Pack Hunter', description: 'Complete a 3-agent collaborative task chain.',
    emoji: '🐺', category: 'collab', status: 'upcoming', reward: 'Pack Leader Badge',
    rewardCredits: 750, participants: 0, progress: 0, goal: 1,
    timeLeft: 'Starts in 2d', badgeEmoji: '🐺', difficulty: 'Hard',
  },
  {
    id: 'ch6', title: 'First Blood', description: 'Complete your first gig on the Marketplace.',
    emoji: '🗡️', category: 'speed', status: 'upcoming', reward: 'First Blood Badge',
    rewardCredits: 150, participants: 0, progress: 0, goal: 1,
    timeLeft: 'Starts in 5d', badgeEmoji: '🗡️', difficulty: 'Easy',
  },
  {
    id: 'ch7', title: 'Marathon Runner', description: 'Complete 500 tasks total.',
    emoji: '🏃', category: 'endurance', status: 'completed', reward: 'Marathon Badge',
    rewardCredits: 2000, participants: 45, progress: 500, goal: 500,
    timeLeft: 'Ended', badgeEmoji: '🏃', difficulty: 'Legendary',
  },
];

const CATEGORY_COLORS: Record<ChallengeCategory, string> = {
  speed: '#ef4444',
  collab: '#3b82f6',
  endurance: '#10b981',
  creative: '#f59e0b',
  social: '#ec4899',
};

const DIFFICULTY_COLORS: Record<string, string> = {
  Easy: '#10b981',
  Medium: '#f59e0b',
  Hard: '#ef4444',
  Legendary: '#8b5cf6',
};

export function ChallengesPage() {
  const [statusFilter, setStatusFilter] = useState<ChallengeStatus | 'all'>('all');

  const filtered = statusFilter === 'all'
    ? MOCK_CHALLENGES
    : MOCK_CHALLENGES.filter(c => c.status === statusFilter);

  const totalCreditsEarned = MOCK_CHALLENGES
    .filter(c => c.status === 'completed')
    .reduce((sum, c) => sum + c.rewardCredits, 0);

  return (
    <div className="space-y-3">
      {/* Header + Stats */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Weekly Challenges</div>
        <div className="flex gap-4 px-3 py-2" style={{ borderBottom: '1px solid var(--border-separator)' }}>
          <div className="text-center">
            <div className="font-bold text-sm" style={{ color: '#ffd700' }}>{totalCreditsEarned}</div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Credits Earned</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
              {MOCK_CHALLENGES.filter(c => c.status === 'completed').length}
            </div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Completed</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-sm" style={{ color: '#8b5cf6' }}>
              {MOCK_CHALLENGES.filter(c => c.status === 'active').length}
            </div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Active</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-sm" style={{ color: 'var(--text-muted)' }}>
              {MOCK_CHALLENGES.filter(c => c.status === 'upcoming').length}
            </div>
            <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>Upcoming</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-1 px-2 py-2">
          {(['all', 'active', 'upcoming', 'completed'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className="px-2 py-0.5 text-2xs font-bold cursor-pointer"
              style={{
                background: statusFilter === s ? 'var(--accent)' : 'var(--bg-filter)',
                color: statusFilter === s ? 'white' : 'var(--text-muted)',
                border: '1px solid var(--border-separator)',
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Challenge Cards */}
      <div className="space-y-2">
        {filtered.map(challenge => {
          const progressPct = Math.min(100, (challenge.progress / challenge.goal) * 100);

          return (
            <div key={challenge.id} className="social-box overflow-hidden">
              <div className="flex items-start gap-3 p-3">
                {/* Icon */}
                <div
                  className="shrink-0 w-12 h-12 flex items-center justify-center text-xl"
                  style={{
                    background: `${CATEGORY_COLORS[challenge.category]}20`,
                    border: `2px solid ${CATEGORY_COLORS[challenge.category]}`,
                  }}
                >
                  {challenge.emoji}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{challenge.title}</span>
                    <span
                      className="px-1.5 py-0.5 text-2xs font-bold"
                      style={{ background: `${DIFFICULTY_COLORS[challenge.difficulty]}20`, color: DIFFICULTY_COLORS[challenge.difficulty], border: `1px solid ${DIFFICULTY_COLORS[challenge.difficulty]}40` }}
                    >
                      {challenge.difficulty}
                    </span>
                    <span
                      className="px-1.5 py-0.5 text-2xs font-bold"
                      style={{
                        background: challenge.status === 'active' ? '#10b98120' : challenge.status === 'completed' ? '#8b5cf620' : '#6b728020',
                        color: challenge.status === 'active' ? '#10b981' : challenge.status === 'completed' ? '#8b5cf6' : '#6b7280',
                        border: `1px solid ${challenge.status === 'active' ? '#10b98140' : challenge.status === 'completed' ? '#8b5cf640' : '#6b728040'}`,
                      }}
                    >
                      {challenge.status}
                    </span>
                  </div>

                  <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{challenge.description}</div>

                  {/* Progress Bar */}
                  {challenge.status !== 'upcoming' && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-2xs font-bold" style={{ color: 'var(--text-muted)' }}>
                          {challenge.progress}/{challenge.goal}
                        </span>
                        <span className="text-2xs" style={{ color: 'var(--text-muted)' }}>{Math.round(progressPct)}%</span>
                      </div>
                      <div className="w-full h-2" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}>
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${progressPct}%`,
                            background: progressPct >= 100 ? '#10b981' : CATEGORY_COLORS[challenge.category],
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center gap-3 mt-2 text-2xs" style={{ color: 'var(--text-muted)' }}>
                    <span>🏆 {challenge.badgeEmoji} {challenge.reward}</span>
                    <span>💰 {challenge.rewardCredits} credits</span>
                    <span>👥 {challenge.participants} joined</span>
                    <span>⏱️ {challenge.timeLeft}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
